import { combineReducers } from 'redux';
import { call, put, takeLatest } from 'redux-saga/effects';
import { createSelector } from 'reselect';
import moment from 'moment';
import paymentApi, { apiCallIds } from '../api';
import {
  createApiActionCreators,
  createActionCreator,
  createReducer,
  createActionType,
  REQUEST,
  SUCCESS,
  FAILURE,
} from '../../../common/utils/reduxHelpers';
import { showModal, hideModal } from '../../../features/modal/ducks';
import { DEBTOR_PAYMENT_MODAL } from '../components/DebtorPaymentModal';
import { LOAN_LIST_MODAL } from '../components/LoanListModal';
import { PAYMENT_SHIFT_MODAL } from '../components/PaymentShiftModal';
import { message } from 'antd';
import { push } from 'react-router-redux';

/**
 * ACTION TYPES
 */
export const FETCH_PAYMENTS = 'paymentManagement/FETCH_PAYMENTS';
export const SET_PAYMENTS_PAGINATION = 'paymentManagement/SET_PAYMENTS_PAGINATION';
export const SET_PAYMENTS_FILTER = 'paymentManagement/SET_PAYMENTS_FILTER';
export const FETCH_PAYMENT = 'paymentManagement/FETCH_PAYMENT';
export const ASSIGN_TO_CREDITOR = 'paymentManagement/ASSIGN_TO_CREDITOR';
export const REFUND_PAYMENT = 'paymentManagement/REFUND_PAYMENT';
export const ASSIGN_TO_INSTALLMENT = 'paymentManagement/ASSIGN_TO_INSTALLMENT';
export const SET_ACCOUNT = 'paymentManagement/SET_ACCOUNT';
export const GET_AVAILABLE_INSTALLMENT = 'paymentManagement/GET_AVAILABLE_INSTALLMENT';
export const DIVIDING_PAYMENT = 'paymentManagement/DIVIDING_PAYMENT';
export const SHIFT_PAYMENT = 'paymentManagement/SHIFT_PAYMENT';

/**
 * ACTIONS
 */
export const fetchPaymentsActions = createApiActionCreators(FETCH_PAYMENTS);
export const setPaymentsPaginationAction = createActionCreator(SET_PAYMENTS_PAGINATION);
export const setPaymentsFilterAction = createActionCreator(SET_PAYMENTS_FILTER);
export const fetchPaymentActions = createApiActionCreators(FETCH_PAYMENT);
export const assignToCreditorActions = createApiActionCreators(ASSIGN_TO_CREDITOR);
export const refundPaymentActions = createApiActionCreators(REFUND_PAYMENT);
export const assignToInstallmentActions = createApiActionCreators(ASSIGN_TO_INSTALLMENT);
export const getAvailableInstallmentActions = createApiActionCreators(GET_AVAILABLE_INSTALLMENT);
export const setAccountAction = createActionCreator(SET_ACCOUNT);
export const dividingPaymentRequest = createActionCreator(DIVIDING_PAYMENT);
export const shiftPaymentActions = createApiActionCreators(SHIFT_PAYMENT);

/**
 * REDUCERS
 */
const initialState = {
  payment: null,
  payments: {
    items: [],
    pagination: {
      current: null,
      pageSize: null,
      total: null,
    },
    filter: {
      status: '',
      transferredAtStart: null,
      transferredAtEnd: null,
    },
  },
  account: null,
};

const payment = createReducer(initialState.payment, {
  [FETCH_PAYMENT]: {
    [SUCCESS]: (state, payload) => payload,
    [FAILURE]: () => null,
  },
  [ASSIGN_TO_CREDITOR]: {
    [SUCCESS]: (state, payload) => payload,
  },
  [REFUND_PAYMENT]: {
    [SUCCESS]: (state, payload) => payload,
  },
  [ASSIGN_TO_INSTALLMENT]: {
    [SUCCESS]: (state, payload) => payload,
  },
  [SHIFT_PAYMENT]: {
    [SUCCESS]: (state, payload) => payload,
  }
});

const payments = createReducer(initialState.payments, {
  [FETCH_PAYMENTS]: {
    [SUCCESS]: (state, payload) => ({
      ...state,
      items: payload.data,
    }),
    [FAILURE]: state => ({
      ...state,
      items: [],
    }),
  },
  [SET_PAYMENTS_PAGINATION]: (state, payload) => ({
    ...state,
    pagination: {
      current: payload.pageNumber + 1,
      pageSize: payload.pageSize,
      total: payload.total,
    },
  }),
  [SET_PAYMENTS_FILTER]: (state, payload) => ({
    ...state,
    filter: payload,
  }),
});

const account = createReducer(initialState.account, {
  [SET_ACCOUNT]: (state, payload) => payload,
});

export default combineReducers({
  payment,
  payments,
  account,
});

/**
 * SELECTORS
 */
export const selectPaymentState = state => state.payment;
export const selectPayment = state => selectPaymentState(state).payment;
export const selectPaymentsState = state => selectPaymentState(state).payments;
export const selectPayments = state => selectPaymentsState(state).items;
export const selectPaymentsPagination = state => selectPaymentsState(state).pagination;
export const selectPaymentsFilter = state => selectPaymentsState(state).filter;
export const selectAccount = state => selectPaymentState(state).account;

export const selectAccountFullName = createSelector(
  selectAccount,
  account => (account ? `${account.nameFirst} ${account.nameLast}` : '')
);

export const selectPaymentsFilterDateRange = createSelector(selectPaymentsFilter, filter => [
  filter.transferredAtStart ? moment(filter.transferredAtStart) : null,
  filter.transferredAtEnd ? moment(filter.transferredAtEnd) : null,
]);

/**
 * SAGAS
 */
function* fetchPayment({ payload }) {
  const resp = yield call(paymentApi.fetchPayment, payload.paymentId);

  if (resp.ok) {
    yield put(fetchPaymentActions.success(resp.data));
  } else {
    yield put(fetchPaymentActions.failure(resp.error));
  }
}

function* fetchPayments({ payload }) {
  const resp = yield call(paymentApi.fetchPayments, payload);

  if (resp.ok) {
    yield put(fetchPaymentsActions.success(resp.data));
    yield put(setPaymentsPaginationAction(resp.data));
  } else {
    yield put(fetchPaymentsActions.failure(resp.error));
  }
}

function* assignToCreditor({ payload }) {
  const resp = yield call(paymentApi.assignToCreditor, payload.paymentId, payload.accountId);

  if (resp.ok) {
    yield put(assignToCreditorActions.success(resp.data));
    yield put(hideModal(apiCallIds.ASSIGN_TO_CREDITOR));
  } else {
    yield put(assignToCreditorActions.failure(resp.error));
  }
}

function* refundPayment({ payload }) {
  console.log(payload)
  const resp = yield call(paymentApi.refundPayment, payload.paymentId, payload.values);

  if (resp.ok) {
    yield put(refundPaymentActions.success(resp.data));
    yield put(hideModal(apiCallIds.REFUND_PAYMENT));
  } else {
    yield put(refundPaymentActions.failure(resp.error));
  }
}

function* dividingPayment({ payload }) {
  const resp = yield call(paymentApi.dividingPayment, payload.paymentId, payload.data);

  if (resp.ok) {
    yield put(message.success('Uložené'));
    yield put(push(`/admin/payments`));
  }
}

function* assignToInstallment({ payload }) {
  const resp = yield call(paymentApi.assignToInstallment, payload.paymentId, payload.loanId);

  if (resp.ok) {
    yield put(assignToInstallmentActions.success(resp.data));
    yield put(hideModal(LOAN_LIST_MODAL));
  } else {
    yield put(assignToInstallmentActions.failure(resp.error));
  }
}

function* getAvailableInstallment({ payload }) {
  const resp = yield call(paymentApi.getAvailableInstallment, payload.paymentId, payload.loanId);

  if (resp.ok) {
    yield put(getAvailableInstallmentActions.success(resp.data));
    yield put(
      assignToInstallmentActions.request({
        paymentId: payload.paymentId,
        loanId: payload.loanId,
      })
    );
  } else {
    yield put(getAvailableInstallmentActions.failure(resp.error));
  }
}

function* transitionToLoanListModal() {
  yield put(hideModal(DEBTOR_PAYMENT_MODAL));
  yield put(showModal({ id: LOAN_LIST_MODAL }));
}

function* shiftPayment ( {payload} ) {
  const resp = yield call(paymentApi.shiftPayment, payload.paymentId, payload.loanId);
  if (resp.ok){
    yield put(shiftPaymentActions.success(resp.data))
    yield put(hideModal(PAYMENT_SHIFT_MODAL));
    yield put(message.success('Presun platby na inú pôžičku bol úspešný'));
    yield put(push(`/admin/loans/${payload.loanId}`));
  }
  else {
    yield put(shiftPaymentActions.failure(resp.error))
  }
}

export function* paymentSaga() {
  yield takeLatest(createActionType(FETCH_PAYMENT, REQUEST), fetchPayment);
  yield takeLatest(createActionType(FETCH_PAYMENTS, REQUEST), fetchPayments);
  yield takeLatest(createActionType(ASSIGN_TO_CREDITOR, REQUEST), assignToCreditor);
  yield takeLatest(createActionType(REFUND_PAYMENT, REQUEST), refundPayment);
  yield takeLatest(createActionType(ASSIGN_TO_INSTALLMENT, REQUEST), assignToInstallment);
  yield takeLatest(createActionType(GET_AVAILABLE_INSTALLMENT, REQUEST), getAvailableInstallment);
  yield takeLatest(DIVIDING_PAYMENT, dividingPayment);
  yield takeLatest(SET_PAYMENTS_FILTER, fetchPayments);
  yield takeLatest(SET_ACCOUNT, transitionToLoanListModal);
  yield takeLatest(createActionType(SHIFT_PAYMENT, REQUEST), shiftPayment);
}



// WEBPACK FOOTER //
// ./src/features/payment-management/ducks/index.js